"use client"

import * as React from "react"
import {
  IconChevronLeft,
  IconChevronRight,
  IconArrowUp,
  IconArrowDown,
  IconArrowsSort,
  IconLoader2,
  IconTableOff,
} from "@tabler/icons-react"
import {
  type ColumnDef,
  type ColumnResizeMode,
  type PaginationState,
  type Row,
  type SortingState,
  type TableOptions,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

// ============================================================================
// Types
// ============================================================================

export interface DataTableColumn<TData> {
  /** Unique identifier for the column */
  id: string
  /** Header text (will be displayed in uppercase) */
  header: string | React.ReactNode | ((props: { column: unknown }) => React.ReactNode)
  /** Accessor key for the data field */
  accessorKey?: keyof TData
  /** Custom cell renderer */
  cell?: (props: { row: Row<TData>; getValue: () => unknown }) => React.ReactNode
  /** Flex grow value (like MUI's flex property). Takes priority over width. */
  flex?: number
  /** Minimum width in pixels */
  minWidth?: number
  /** Maximum width in pixels */
  maxWidth?: number
  /** Initial width in pixels (used when flex is not set) */
  width?: number
  /** Enable/disable resizing for this column */
  enableResizing?: boolean
  /** Enable/disable sorting for this column */
  enableSorting?: boolean
  /** Text alignment for cell content */
  align?: "left" | "center" | "right"
}

export interface DataTablePaginationConfig {
  /** Enable pagination */
  enabled: boolean
  /** Pagination mode: 'client' for frontend pagination, 'server' for backend pagination */
  mode: "client" | "server"
  /** Page size options */
  pageSizeOptions?: number[]
  /** Default page size */
  defaultPageSize?: number
  /** Current page index (0-based) - required for server mode */
  pageIndex?: number
  /** Current page size - required for server mode */
  pageSize?: number
  /** Total number of items - required for server mode */
  totalItems?: number
  /** Callback when page changes - required for server mode */
  onPageChange?: (pageIndex: number) => void
  /** Callback when page size changes - required for server mode */
  onPageSizeChange?: (pageSize: number) => void
}

export interface DataTableProps<TData> {
  /** Array of data to display */
  data: TData[]
  /** Column definitions */
  columns: DataTableColumn<TData>[]
  /** Pagination configuration - omit or set enabled: false to hide pagination */
  pagination?: DataTablePaginationConfig
  /** Enable column resizing */
  enableColumnResizing?: boolean
  /** Column resize mode */
  columnResizeMode?: ColumnResizeMode
  /** Enable sorting globally */
  enableSorting?: boolean
  /** Loading state */
  loading?: boolean
  /** Empty state message */
  emptyMessage?: string
  /** Empty state icon (optional, defaults to IconTableOff) */
  emptyIcon?: React.ReactNode
  /** Custom row key extractor */
  getRowId?: (row: TData) => string
  /** Additional class name for the table container */
  className?: string
  /** Callback when row is clicked */
  onRowClick?: (row: TData) => void
  /** Custom row class name getter */
  getRowClassName?: (row: TData) => string
  /** Callback when sorting changes */
  onSortingChange?: (sorting: SortingState) => void
  /** Controlled sorting state */
  sorting?: SortingState
  /** Footer rows to display at the bottom of the table (non-sortable, non-paginated) */
  footerData?: TData[]
}

// ============================================================================
// Loading Component
// ============================================================================

function TableLoader() {
  return (
    <tr>
      <td colSpan={100} className="py-16">
        <div className="flex flex-col items-center justify-center gap-3">
          <IconLoader2 className="size-8 text-primary animate-spin" />
          <span className="text-sm text-muted-foreground">Loading data...</span>
        </div>
      </td>
    </tr>
  )
}

// ============================================================================
// Empty State Component
// ============================================================================

interface EmptyStateProps {
  message: string
  icon?: React.ReactNode
  colSpan: number
}

function EmptyState({ message, icon, colSpan }: EmptyStateProps) {
  return (
    <tr>
      <td colSpan={colSpan} className="py-16">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex items-center justify-center size-12 rounded-full bg-muted">
            {icon || <IconTableOff className="size-6 text-muted-foreground" />}
          </div>
          <span className="text-sm text-muted-foreground">{message}</span>
        </div>
      </td>
    </tr>
  )
}

// ============================================================================
// Pagination Component
// ============================================================================

interface TablePaginationProps {
  pageIndex: number
  pageSize: number
  totalItems: number
  totalPages: number
  pageSizeOptions: number[]
  canPreviousPage: boolean
  canNextPage: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

function TablePagination({
  pageIndex,
  pageSize,
  totalItems,
  totalPages,
  pageSizeOptions,
  canPreviousPage,
  canNextPage,
  onPageChange,
  onPageSizeChange,
}: TablePaginationProps) {
  const startItem = totalItems === 0 ? 0 : pageIndex * pageSize + 1
  const endItem = Math.min((pageIndex + 1) * pageSize, totalItems)

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages + 2) {
      // Show all pages if total is small
      for (let i = 0; i < totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(0)

      if (pageIndex > 2) {
        pages.push("ellipsis")
      }

      // Show pages around current page
      const start = Math.max(1, pageIndex - 1)
      const end = Math.min(totalPages - 2, pageIndex + 1)

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i)
        }
      }

      if (pageIndex < totalPages - 3) {
        pages.push("ellipsis")
      }

      // Always show last page
      if (!pages.includes(totalPages - 1)) {
        pages.push(totalPages - 1)
      }
    }

    return pages
  }

  return (
    <div className="flex items-center justify-between h-[44px] px-[16px] border-t border-[#EDEEF3] bg-[#F7F8F8]">
      <div className="flex items-center gap-3">
        <Label htmlFor="rows-per-page" className="text-[12px] text-[#8A8F98] whitespace-nowrap uppercase tracking-tight font-medium">
          Rows per page
        </Label>
        <Select
          value={`${pageSize}`}
          onValueChange={(value) => onPageSizeChange(Number(value))}
        >
          <SelectTrigger size="xs" className="border-[#EDEEF3] bg-white shadow-none focus:ring-0 focus:border-[#654EDA]" id="rows-per-page">
            <SelectValue placeholder={pageSize} />
          </SelectTrigger>
          <SelectContent side="top" className="border-[#EDEEF3] shadow-lg">
            {pageSizeOptions.map((size) => (
              <SelectItem key={size} value={`${size}`} className="text-[12px]">
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-[12px] text-[#8A8F98] font-medium uppercase tracking-tight">
        {startItem}-{endItem} of {totalItems}
        </span>
      </div>

      <div className="flex items-center gap-1">
        {/* Previous button */}
        <Button
          variant="ghost"
          size="icon"
          className="size-[28px] text-[#8A8F98] hover:text-[#08090A] hover:bg-[#EDEEF3]"
          onClick={() => onPageChange(pageIndex - 1)}
          disabled={!canPreviousPage}
        >
          <span className="sr-only">Go to previous page</span>
          <IconChevronLeft className="size-3.5" />
        </Button>

        {/* Page numbers */}
        {getPageNumbers().map((page, index) =>
          page === "ellipsis" ? (
            <span key={`ellipsis-${index}`} className="px-1 text-[12px] text-[#8A8F98]">
              ...
            </span>
          ) : (
            <Button
              key={page}
              variant={pageIndex === page ? "default" : "ghost"}
              size="icon"
              className={cn(
                "size-[24px] text-[12px] font-medium rounded-[4px]",
                pageIndex === page 
                  ? "bg-[#654EDA] text-white hover:bg-[#654EDA]/90" 
                  : "text-[#8A8F98] hover:text-[#08090A] hover:bg-[#EDEEF3]"
              )}
              onClick={() => onPageChange(page)}
            >
              {page + 1}
            </Button>
          )
        )}

        {/* Next button */}
        <Button
          variant="ghost"
          size="icon"
          className="size-[28px] text-[#8A8F98] hover:text-[#08090A] hover:bg-[#EDEEF3]"
          onClick={() => onPageChange(pageIndex + 1)}
          disabled={!canNextPage}
        >
          <span className="sr-only">Go to next page</span>
          <IconChevronRight className="size-3.5" />
        </Button>
      </div>
    </div>
  )
}

// ============================================================================
// Resize Handle Component
// ============================================================================

interface ResizeHandleProps {
  onMouseDown: (e: React.MouseEvent) => void
  onTouchStart: (e: React.TouchEvent) => void
  isResizing: boolean
}

function ResizeHandle({ onMouseDown, onTouchStart, isResizing }: ResizeHandleProps) {
  return (
    <div
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      className={cn(
        "absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none",
        "hover:bg-primary/50 transition-colors",
        isResizing && "bg-primary"
      )}
    />
  )
}

// ============================================================================
// Sort Icon Component (shows on hover)
// ============================================================================

interface SortIconProps {
  isSorted: false | "asc" | "desc"
  canSort: boolean
}

function SortIcon({ isSorted, canSort }: SortIconProps) {
  if (!canSort) return null

  return (
    <span
      className={cn(
        "ml-1.5 inline-flex transition-opacity",
        isSorted ? "opacity-100" : "opacity-0 group-hover:opacity-60"
      )}
    >
      {isSorted === "asc" ? (
        <IconArrowUp className="size-3.5" />
      ) : isSorted === "desc" ? (
        <IconArrowDown className="size-3.5" />
      ) : (
        <IconArrowsSort className="size-3.5" />
      )}
    </span>
  )
}

// ============================================================================
// Main DataTable Component
// ============================================================================

export function DataTable<TData>({
  data,
  columns: columnDefs,
  pagination,
  enableColumnResizing = true,
  columnResizeMode = "onChange",
  enableSorting = true,
  loading = false,
  emptyMessage = "No results found.",
  emptyIcon,
  getRowId,
  className,
  onRowClick,
  getRowClassName,
  onSortingChange,
  sorting: externalSorting,
  footerData,
}: DataTableProps<TData>) {
  // Check if any column uses flex
  const useFlexLayout = columnDefs.some((col) => col.flex !== undefined)

  // Calculate total flex for distribution
  const totalFlex = React.useMemo(() => {
    return columnDefs.reduce((sum, col) => sum + (col.flex ?? 0), 0)
  }, [columnDefs])

  // Convert our column definitions to tanstack format
  const columns: ColumnDef<TData>[] = React.useMemo(
    () =>
      columnDefs.map((col): ColumnDef<TData> => ({
        id: col.id,
        accessorKey: col.accessorKey as string,
        header: col.header as ColumnDef<TData>["header"],
        cell: col.cell
          ? ({ row, getValue }) => col.cell!({ row, getValue })
          : ({ getValue }) => {
              const value = getValue()
              return <span>{value as string}</span>
            },
        size: col.width,
        minSize: col.minWidth ?? 50,
        maxSize: col.maxWidth ?? 1000,
        enableResizing: col.enableResizing ?? true,
        enableSorting: col.enableSorting ?? true,
        meta: {
          align: col.align ?? "left",
          flex: col.flex,
        },
      })),
    [columnDefs]
  )

  // Sorting state - use external if provided, otherwise internal
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([])
  const sorting = externalSorting !== undefined ? externalSorting : internalSorting
  
  const handleSortingChange = React.useCallback((newSorting: SortingState | ((prev: SortingState) => SortingState)) => {
    const updatedSorting = typeof newSorting === 'function' ? newSorting(sorting) : newSorting
    if (externalSorting === undefined) {
      setInternalSorting(updatedSorting)
    }
    onSortingChange?.(updatedSorting)
  }, [externalSorting, sorting, onSortingChange])

  // Pagination state for client mode
  const [clientPagination, setClientPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: pagination?.defaultPageSize ?? 5,
  })

  // Determine pagination values based on mode
  const isServerMode = pagination?.mode === "server"
  const currentPageIndex = isServerMode
    ? (pagination?.pageIndex ?? 0)
    : clientPagination.pageIndex
  const currentPageSize = isServerMode
    ? (pagination?.pageSize ?? 10)
    : clientPagination.pageSize

  // Table options
  const tableOptions: TableOptions<TData> = {
    data,
    columns,
    columnResizeMode,
    enableColumnResizing,
    enableSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
    },
    onSortingChange: handleSortingChange,
    ...(getRowId && { getRowId }),
  }

  // Add pagination model based on mode
  if (pagination?.enabled) {
    if (isServerMode) {
      tableOptions.manualPagination = true
      tableOptions.pageCount = Math.ceil(
        (pagination.totalItems ?? 0) / currentPageSize
      )
      tableOptions.state = {
        ...tableOptions.state,
        pagination: {
          pageIndex: currentPageIndex,
          pageSize: currentPageSize,
        },
      }
    } else {
      tableOptions.getPaginationRowModel = getPaginationRowModel()
      tableOptions.state = {
        ...tableOptions.state,
        pagination: clientPagination,
      }
      tableOptions.onPaginationChange = setClientPagination
    }
  }

  const table = useReactTable(tableOptions)

  // Handle page changes
  const handlePageChange = (page: number) => {
    if (isServerMode) {
      pagination?.onPageChange?.(page)
    } else {
      setClientPagination((prev) => ({ ...prev, pageIndex: page }))
    }
  }

  const handlePageSizeChange = (size: number) => {
    if (isServerMode) {
      pagination?.onPageSizeChange?.(size)
    } else {
      setClientPagination({ pageIndex: 0, pageSize: size })
    }
  }

  // Calculate pagination values
  // For server mode, if totalItems is not provided, fallback to data.length
  const totalItems = isServerMode
    ? (pagination?.totalItems ?? data.length)
    : data.length
  const totalPages = Math.ceil(totalItems / currentPageSize)

  // Get column style based on flex or width
  const getColumnStyle = (
    colMeta: { flex?: number } | undefined,
    size: number
  ): React.CSSProperties => {
    const flex = colMeta?.flex
    if (useFlexLayout && flex !== undefined) {
      return {
        flex: flex,
        minWidth: 0,
      }
    }
    return {
      width: size,
    }
  }

  return (
    <div className={cn("rounded-[8px] border border-[#EDEEF3] overflow-hidden bg-white shadow-none", className)}>
      <div className="overflow-x-auto">
        <table
          className={cn(
            "w-full text-[13px]",
            useFlexLayout && "table-fixed"
          )}
          style={
            useFlexLayout
              ? { width: "100%", tableLayout: "fixed" }
              : { width: table.getCenterTotalSize() }
          }
        >
          {/* Colgroup for flex layout */}
          {useFlexLayout && (
            <colgroup>
              {table.getAllColumns().map((column) => {
                const meta = column.columnDef.meta as { flex?: number } | undefined
                const flex = meta?.flex
                // Calculate percentage width based on flex values
                const width = flex !== undefined && totalFlex > 0
                  ? `${(flex / totalFlex) * 100}%`
                  : undefined
                return (
                  <col
                    key={column.id}
                    style={{ width }}
                  />
                )
              })}
            </colgroup>
          )}

          {/* Table Header */}
          <thead className="bg-[#F7F8F8] border-b border-[#EDEEF3]">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as { align?: string; flex?: number } | undefined
                  const canSort = header.column.getCanSort()
                  const isSorted = header.column.getIsSorted()

                  return (
                    <th
                      key={header.id}
                      className={cn(
                        "group relative h-[44px] px-[16px] text-left text-[13px] font-medium text-[#8A8F98] uppercase tracking-wide whitespace-nowrap",
                        canSort && "cursor-pointer select-none hover:bg-[#F0F1F1] transition-colors"
                      )}
                      style={getColumnStyle(meta, header.getSize())}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    >
                      <div className="flex items-center">
                        {header.isPlaceholder
                          ? null
                          : typeof header.column.columnDef.header === "string"
                            ? header.column.columnDef.header
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        <SortIcon isSorted={isSorted} canSort={canSort} />
                      </div>
                      {enableColumnResizing && header.column.getCanResize() && !useFlexLayout && (
                        <ResizeHandle
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          isResizing={header.column.getIsResizing()}
                        />
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>

          {/* Table Body */}
          <tbody className="divide-y divide-[#EDEEF3]">
            {loading ? (
              <TableLoader />
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={cn(
                    "h-[44px] transition-colors hover:bg-[#F7F8F8]",
                    onRowClick && "cursor-pointer",
                    getRowClassName?.(row.original)
                  )}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as { align?: string; flex?: number } | undefined
                    const align = meta?.align ?? "left"
                    return (
                      <td
                        key={cell.id}
                        className={cn(
                          "px-[16px] text-[#08090A] font-normal",
                          align === "right" && "text-right",
                          align === "center" && "text-center"
                        )}
                        style={getColumnStyle(meta, cell.column.getSize())}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))
            ) : (
              <EmptyState 
                message={emptyMessage} 
                icon={emptyIcon} 
                colSpan={columns.length} 
              />
            )}

            {/* Footer Rows (if any) */}
            {footerData && footerData.length > 0 && footerData.map((row, rowIndex) => (
              <tr
                key={`footer-${rowIndex}`}
                className={cn(
                  "h-[44px] transition-colors border-t-2 border-[#EDEEF3] bg-[#F7F8F8]/50",
                  getRowClassName?.(row)
                )}
              >
                {table.getVisibleFlatColumns().map((column) => {
                  const meta = column.columnDef.meta as { align?: string; flex?: number } | undefined
                  const align = meta?.align ?? "left"
                  
                  // Create a mock cell context for flexRender
                  const cellValue = (row as any)[column.id] || (column.columnDef as any).accessorKey ? (row as any)[(column.columnDef as any).accessorKey] : undefined
                  
                  return (
                    <td
                      key={`footer-cell-${column.id}`}
                      className={cn(
                        "px-[16px] text-[#08090A]",
                        align === "right" && "text-right",
                        align === "center" && "text-center"
                      )}
                      style={getColumnStyle(meta, column.getSize())}
                    >
                      {flexRender(
                        column.columnDef.cell,
                        {
                          row: { original: row } as any,
                          column: column as any,
                          table: table as any,
                          getValue: () => cellValue,
                          renderValue: () => cellValue,
                        } as any
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination?.enabled && !loading && (
        <TablePagination
          pageIndex={currentPageIndex}
          pageSize={currentPageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          pageSizeOptions={pagination.pageSizeOptions ?? [5, 10, 20, 50]}
          canPreviousPage={currentPageIndex > 0}
          canNextPage={currentPageIndex < totalPages - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  )
}

// ============================================================================
// Re-export types for convenience
// ============================================================================

export type { ColumnDef, Row } from "@tanstack/react-table"
